import { WeatherComponent } from 'app/__generated__/global';
import { findLowestSampleRateVariable } from 'app/helpers/weatherProcessSchemaUtils';
import { NoiseEvent } from 'app/business-logic/services/noise-service';
import { fetchWeatherProcessSchemaProcessVariables } from 'app/business-logic/services/process-schema-services';
import { useNoiseMonitors } from 'app/views/monitoring/useNoiseMonitors';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useWeatherProcessSchemaProcessVariables } from '../../pages/event-history/use-event-history-data';

type WeatherProcessVariable = Awaited<
  ReturnType<typeof fetchWeatherProcessSchemaProcessVariables>
>[number]['processVariables'][number];

export const useEventHistoryWeatherData = ({ data }: { data: NoiseEvent[] | undefined }) => {
  const noiseMonitors = useNoiseMonitors();

  const monitors = useMemo(() => {
    const locationIds = data?.map(event => event.locationId) ?? [];
    return noiseMonitors.filter(monitor => monitor.omnisLocationId && locationIds.includes(monitor.omnisLocationId));
  }, [data, noiseMonitors]);
  const times = data?.map(event => event.startTime ?? '') ?? [];

  const timesMillis = times.map(time => DateTime.fromISO(time).toMillis());
  const startTime = Math.min(...timesMillis);
  const endTime = Math.max(...timesMillis);

  const { data: weatherProcessSchemaProcessVariables = [] } = useWeatherProcessSchemaProcessVariables({
    monitorIds: monitors.map(monitor => monitor.id),
    timeBetween: {
      startTime: DateTime.fromMillis(startTime).toISO() ?? '',
      endTime: DateTime.fromMillis(endTime).toISO() ?? '',
    },
  });

  return useMemo(() => {
    const weatherDataByMonitorId = Object.fromEntries(
      weatherProcessSchemaProcessVariables.map(weatherProcessSchema => {
        const variables = weatherProcessSchema?.processVariables ?? [];
        const windSpeed = findLowestSampleRateVariable<WeatherProcessVariable>(
          variables.filter(variable => variable.component === WeatherComponent.WindSpeed)
        );
        const windDirection = findLowestSampleRateVariable<WeatherProcessVariable>(
          variables.filter(variable => variable.component === WeatherComponent.WindDirection)
        );
        const rainfall = findLowestSampleRateVariable<WeatherProcessVariable>(
          variables.filter(variable => variable.component === WeatherComponent.RainGauge)
        );
        return [
          weatherProcessSchema.processId,
          {
            windSpeed: {
              data: windSpeed?.data,
              displayUnit: windSpeed?.displayUnit.name,
            },
            windDirection: {
              data: windDirection?.data,
              displayUnit: windDirection?.displayUnit.name,
            },
            rainfall: {
              data: rainfall?.data,
              displayUnit: rainfall?.displayUnit.name,
            },
          },
        ];
      })
    );

    const getWeatherDataForLocationId = (locationId: number) => {
      const monitor = monitors.find(m => m.omnisLocationId === locationId);
      if (!monitor) return undefined;
      return weatherDataByMonitorId[monitor.id];
    };

    return {
      weatherDataByMonitorId,
      getWeatherDataForLocationId,
    };
  }, [weatherProcessSchemaProcessVariables, monitors]);
};
